import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { WeddingQuoteInput, VenueInquiryInput, PartialLeadInput } from "@/lib/plan-wizard-schema";
import { estimateLeadValue } from "@/lib/analytics";
import { bridgeLeadToGhl, trackLeadConversion } from "@/lib/lead-tracking";
import { getAttribution, getFirstLanding } from "@/lib/attribution";
import { leadChannel } from "@/lib/lead-channel";
import { leadCategoryForPlanService } from "../../supabase/functions/meta-capi/lead-category.ts";

type EventType = Database["public"]["Enums"]["event_type"];
type EventInsert = Database["public"]["Tables"]["events"]["Insert"];
/** events gained service_style/bar_service/dietary in #227's migration; the
 * generated types pick them up on the next types:sync. */
type WizardEventRow = EventInsert & {
  event_time?: string | null;
  service_style?: string | null;
  bar_service?: string | null;
  dietary?: string[] | null;
};

export interface SubmitResult {
  event_id: string;
  event_number: string;
}

interface WeddingPayload extends WeddingQuoteInput {
  menu_id?: string | null;
  source_page_slug?: string | null;
  /** When the wizard captured a partial lead first (#226), upgrade that row. */
  partial_event_id?: string | null;
}

type VenuePayload = VenueInquiryInput & {
  partial_event_id?: string | null;
};

/** Context the partial-save needs beyond the contact fields. */
export interface PartialLeadContext {
  event_type: EventType;
  source_page_slug?: string | null;
  venue_interest?: boolean;
}

/**
 * Build the structured notes block for wedding/live-service submissions.
 * Kept as plain text so staff see it inline in the admin detail panel
 * without needing a custom renderer.
 */
function buildWeddingNotes(input: WeddingPayload): string {
  const lines: string[] = [];
  // Drop-off delivery/pickup (#1221): submit_wizard_event has no column for it.
  if (input.fulfillment) lines.push(`Fulfillment: ${input.fulfillment === "pickup" ? "Pickup" : "Delivery"}`);
  if (input.event_type_name?.trim()) lines.push(`Event type: ${input.event_type_name.trim()}`);
  if (input.venue_location?.trim()) {
    const label = input.service === "drop_off" ? "Delivery address / area" : "Venue / location";
    lines.push(`${label}: ${input.venue_location.trim()}`);
  }
  if (input.service_style) lines.push(`Service style: ${input.service_style.replace(/_/g, " ")}`);
  if (input.bar_service) lines.push(`Bar service: ${input.bar_service}`);
  if (input.dietary?.length) lines.push(`Dietary: ${input.dietary.join(", ")}`);
  if (input.notes?.trim()) lines.push(`\nAdditional notes:\n${input.notes.trim()}`);
  return lines.join("\n");
}

/** UUID for Lead dedup. Prefers crypto.randomUUID; falls back on insecure
 * contexts (non-HTTPS, non-localhost) where it's unavailable, so tracking
 * isn't silently dropped. */
function newDedupId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // fall through to the manual generator below
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** First-touch attribution, first landing and the derived channel (#1544), for submit_wizard_event's jsonb payload. */
function wizardLeadSource() {
  const source = { ...getAttribution(), ...getFirstLanding() };
  return { ...source, lead_channel: leadChannel(source) };
}

/**
 * Partial-lead capture (issue #226, epic #230). The wizard calls this once a
 * visitor has typed a usable name + email, so an abandoned form still becomes a
 * contactable lead. Inserts an `events` row flagged `is_partial = true`
 * (status 'new'). Crucially, NO confirmation email / GHL bridge / pixel fires
 * here — those run only on full completion. Returns the new row id so completion
 * can upgrade the same row instead of creating a duplicate.
 */
export function useSavePartialLead() {
  return useMutation<string, Error, PartialLeadInput & PartialLeadContext>({
    mutationFn: async (input) => {
      const result = await submitWizardRpc(
        {
          event_type: input.event_type,
          source_page_slug: input.source_page_slug ?? null,
          venue_interest: input.venue_interest ?? false,
          client_name: input.client_name,
          client_email: input.client_email,
          client_phone: input.client_phone ?? null,
          // First-touch attribution (#204) plus first landing and channel (#1544).
          ...wizardLeadSource(),
        },
        { savePartial: true },
      );
      return result.id;
    },
  });
}

/**
 * Persist a completed wizard submission. If a partial row was captured earlier
 * (#226), upgrade THAT row (clearing `is_partial`) so there's no duplicate;
 * otherwise insert fresh. If the partial was already completed/removed, fall back
 * to a fresh insert so a user's submit can never fail. Then fire the one-time
 * side effects (Meta pixel/CAPI, GHL bridge — which also owns the confirmation
 * email since #224) exactly once.
 */
async function persistCompletedEvent(
  row: WizardEventRow,
  partialEventId: string | null | undefined,
): Promise<SubmitResult> {
  // Writes go through the submit_wizard_event RPC (SECURITY DEFINER): anon has
  // no SELECT policy on events, so a plain INSERT/UPDATE … RETURNING (what
  // `.select()` produces) fails RLS for logged-out visitors — the wizard's
  // entire audience. The RPC whitelists columns, forces status/source
  // server-side, applies the only-still-partial upgrade rule, and returns
  // (id, event_number) without widening anon's readable surface.
  const event = await submitWizardRpc(row, { partialEventId });

  // Fire-and-forget: intentionally not awaited so the mutation resolves
  // immediately and the UI navigates to the menu (#1221). Both calls start
  // their requests here, before the mutation resolves, so the client-side
  // navigation can't drop them. Errors are swallowed.
  // The confirmation email rides the GHL bridge server-side (#224). The Lead
  // carries an estimated value from the guest count for value-based bidding (#278)
  // and its funnel, from the wizard service, for per-funnel conversions (#925).
  trackLeadConversion({
    rowId: event.id,
    source: "event",
    dedupId: newDedupId(),
    value: estimateLeadValue(row.guest_count),
    contentCategory: leadCategoryForPlanService(row.event_type),
  });
  bridgeLeadToGhl("event", event.id);

  return { event_id: event.id, event_number: event.event_number };
}

interface WizardRpcResult {
  id: string;
  event_number: string;
}

// The generated types learn about submit_wizard_event on the next types:sync
// (post-merge); until then this typed shim keeps the call lint/tsc-clean
// without reaching for `any`.
type WizardRpc = (
  fn: "submit_wizard_event",
  args: { _event: Record<string, unknown>; _partial_event_id: string | null; _save_partial: boolean },
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

async function submitWizardRpc(
  row: WizardEventRow,
  opts: { partialEventId?: string | null; savePartial?: boolean } = {},
): Promise<WizardRpcResult> {
  // status / source / is_partial are forced inside the RPC; everything else
  // rides the whitelisted jsonb payload.
  const { status: _status, source: _source, ...payload } = row;
  const rpc = supabase.rpc.bind(supabase) as unknown as WizardRpc;
  const { data, error } = await rpc("submit_wizard_event", {
    _event: payload,
    _partial_event_id: opts.partialEventId ?? null,
    _save_partial: opts.savePartial ?? false,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as WizardRpcResult[];
  if (!rows[0]?.id) throw new Error("Submission failed — please try again.");
  return rows[0];
}

export function useSubmitWeddingQuote() {
  return useMutation<SubmitResult, Error, WeddingPayload>({
    mutationFn: async (input) => {
      const event_type: EventType = input.service;
      return persistCompletedEvent(
        {
          event_type,
          status: "new",
          source: "plan_wizard",
          source_page_slug: input.source_page_slug ?? null,
          client_name: input.client_name,
          client_email: input.client_email,
          client_phone: input.client_phone ?? null,
          event_date: input.event_date,
          event_time: input.event_time || null,
          guest_count: input.guest_count,
          menu_id: input.menu_id ?? null,
          // Preferences land as real columns (#227); the notes block keeps a
          // readable mirror for staff.
          service_style: input.service_style ?? null,
          bar_service: input.bar_service ?? null,
          dietary: input.dietary?.length ? input.dietary : null,
          notes: buildWeddingNotes(input),
          // First-touch attribution (#204) plus first landing and channel (#1544).
          ...wizardLeadSource(),
        },
        input.partial_event_id,
      );
    },
  });
}

export function useSubmitVenueInquiry() {
  return useMutation<SubmitResult, Error, VenuePayload>({
    mutationFn: async (input) => {
      const note = `Event type: ${input.event_type_note.replace(/_/g, " ")}`;
      return persistCompletedEvent(
        {
          event_type: "venue",
          status: "new",
          source: "plan_wizard",
          source_page_slug: "venue",
          venue_interest: true,
          client_name: input.client_name,
          client_email: input.client_email,
          client_phone: input.client_phone ?? null,
          event_date: input.event_date,
          guest_count: input.guest_count,
          notes: note,
          // First-touch attribution (#204) plus first landing and channel (#1544).
          ...wizardLeadSource(),
        },
        input.partial_event_id,
      );
    },
  });
}
